import {
  ComunicadoSistemaCriteria,
  ComunicadoSistemaFiltrosOptions,
  ComunicadoSistemaResumo,
} from "@workflow/types";

import { httpClient } from "./httpClient";
import { buildQueryParams } from "@/utils/build-query-params";
import { PageResponse } from "@/types/http.type";
import {
  ComunicadoSistemaDTO,
  ComunicadoSistemaHistoricoDTO,
  ComunicadoSistemaPayload,
  ComunicadoSistemaPendentesDTO,
  ComunicadoSistemaVigenteDTO,
} from "@/types/comunicado-sistema.type";

class ComunicadoSistemaService {
  private basePath = "/api/v1/comunicados-sistema";

  /** Vigentes agora — o que o usuário vê (barra/sino/mural). */
  async listVigentes(): Promise<ComunicadoSistemaVigenteDTO[]> {
    return httpClient.get<ComunicadoSistemaVigenteDTO[]>(`${this.basePath}/vigentes`);
  }

  /** Histórico do usuário comum: tudo que já esteve no ar para ele (expirados/arquivados inclusos). */
  async listHistorico(
    criteria?: ComunicadoSistemaCriteria,
  ): Promise<PageResponse<ComunicadoSistemaHistoricoDTO>> {
    let url = `${this.basePath}/historico`;
    if (criteria) {
      const query = buildQueryParams(criteria);
      if (query) url += "?" + query;
    }
    return httpClient.get<PageResponse<ComunicadoSistemaHistoricoDTO>>(url);
  }

  /** Opções do funil de filtro do histórico (sem rascunho/agendado). */
  async getHistoricoFiltros(): Promise<ComunicadoSistemaFiltrosOptions> {
    return httpClient.get<ComunicadoSistemaFiltrosOptions>(`${this.basePath}/historico/filtros`);
  }

  /** Lista paginada + filtrada (tela admin). */
  async listagem(
    criteria?: ComunicadoSistemaCriteria,
  ): Promise<PageResponse<ComunicadoSistemaDTO>> {
    let url = `${this.basePath}/listagem`;
    if (criteria) {
      const query = buildQueryParams(criteria);
      if (query) url += "?" + query;
    }
    return httpClient.get<PageResponse<ComunicadoSistemaDTO>>(url);
  }

  /** Opções do funil de filtro. */
  async getFiltros(): Promise<ComunicadoSistemaFiltrosOptions> {
    return httpClient.get<ComunicadoSistemaFiltrosOptions>(`${this.basePath}/filtros`);
  }

  /** Contadores por estado (KPIs). */
  async getResumo(): Promise<ComunicadoSistemaResumo> {
    return httpClient.get<ComunicadoSistemaResumo>(`${this.basePath}/resumo`);
  }

  async getById(id: number): Promise<ComunicadoSistemaDTO> {
    return httpClient.get<ComunicadoSistemaDTO>(`${this.basePath}/${id}`);
  }

  async create(payload: ComunicadoSistemaPayload): Promise<ComunicadoSistemaDTO> {
    return httpClient.post<ComunicadoSistemaDTO>(`${this.basePath}`, payload, {
      toastConfig: { successMessage: "Comunicado criado." },
    });
  }

  async update(id: number, payload: ComunicadoSistemaPayload): Promise<ComunicadoSistemaDTO> {
    return httpClient.put<ComunicadoSistemaDTO>(`${this.basePath}/${id}`, payload, {
      toastConfig: { successMessage: "Comunicado atualizado." },
    });
  }

  async publicar(id: number): Promise<ComunicadoSistemaDTO> {
    return httpClient.patch<ComunicadoSistemaDTO>(`${this.basePath}/${id}/publicar`, undefined, {
      toastConfig: { successMessage: "Comunicado publicado." },
    });
  }

  async arquivar(id: number): Promise<ComunicadoSistemaDTO> {
    return httpClient.patch<ComunicadoSistemaDTO>(`${this.basePath}/${id}/arquivar`, undefined, {
      toastConfig: { successMessage: "Comunicado arquivado." },
    });
  }

  async remove(id: number): Promise<void> {
    await httpClient.delete<void>(`${this.basePath}/${id}`, {
      toastConfig: { successMessage: "Comunicado removido." },
    });
  }

  /** Registra o "Li e aceito" do usuário atual (comunicado crítico). */
  async aceitar(id: number): Promise<void> {
    await httpClient.post<{ ok: boolean }>(`${this.basePath}/${id}/aceite`, undefined, {
      toastConfig: { successMessage: "Leitura confirmada." },
    });
  }

  /** Painel admin: quem aceitou e quem falta. */
  async listPendentes(id: number): Promise<ComunicadoSistemaPendentesDTO> {
    return httpClient.get<ComunicadoSistemaPendentesDTO>(`${this.basePath}/${id}/pendentes`);
  }
}

export const comunicadoSistemaService = new ComunicadoSistemaService();
